/**
 * ComposerCaptionTabs — primary per-platform caption editor.
 *
 * Ports the AI-Studio pattern into the Composer / Post-Edit flow:
 * instead of a single shared textarea (where users awkwardly glue
 * `**Twitter:**`, `**Facebook:**` markers into one blob), we render
 * one tab per selected platform and bind the textarea to that
 * platform's `platform_overrides[id].text` entry.
 *
 * Design rules (match StudioPostCanvas.tsx):
 * - One tab per `selectedPlatforms` entry. Tabs scroll horizontally
 *   when there are many.
 * - Active-tab textarea edits `overrides[activeTab].text` only. When
 *   the override hasn't been authored yet the textarea pre-fills
 *   with the shared caption so the first write forks from there —
 *   this keeps the "I wrote generic copy and now I'm tailoring" flow
 *   from losing the existing text.
 * - When 0 platforms are selected, we render a prompt pointing the
 *   user back to the platforms picker on the right rail. Previously
 *   this was invisible — users didn't know selection was required
 *   to review/edit per-platform.
 * - A "Clear" button on the active tab removes the override entirely.
 * - "Auto-Adapt AI" surfaces when any platform is over its char
 *   limit — same mutation the legacy `PlatformContentTabs` used.
 * - Char count & over-limit colour come from the active platform's
 *   own `maxChars`, matching Studio's behaviour.
 *
 * Writes: this component is stateless about storage — parent owns
 * `platformOverrides` and `caption`. We callback both on every change
 * so downstream systems (preflight, char-count warnings, live
 * preview) stay wired to a single source of truth.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Sparkles, RotateCcw, AlertTriangle } from 'lucide-react';
import { aiAPI, getErrorMessage } from '../../services/api';
import { useThemeStore } from '../../store/themeStore';
import StudioCaptionCharCount from '../studio/StudioCaptionCharCount';
import { PLATFORMS } from './types';

/** Available tones — passed verbatim to `aiAPI.adaptForPlatforms`'s
 *  optional ``tone`` parameter. Keep in sync with backend prompt
 *  templates in `/ai/adapt-for-platforms`. */
const TONE_OPTIONS = [
  { id: '', labelKey: 'comp_tone_auto', fallback: 'Auto' },
  { id: 'professional', labelKey: 'comp_tone_professional', fallback: 'Professional' },
  { id: 'casual', labelKey: 'comp_tone_casual', fallback: 'Casual' },
  { id: 'friendly', labelKey: 'comp_tone_friendly', fallback: 'Friendly' },
  { id: 'funny', labelKey: 'comp_tone_funny', fallback: 'Funny' },
  { id: 'authoritative', labelKey: 'comp_tone_authoritative', fallback: 'Authoritative' },
] as const;

interface Props {
  /** Shared caption — used as the fallback when a platform hasn't
   *  been tailored yet, and mirrored back on every edit so legacy
   *  systems (preflight, first-comment char count) keep working. */
  caption: string;
  onCaptionChange: (next: string) => void;

  selectedPlatforms: string[];
  /** Inline platform selection (primary, discoverable). The right-
   *  rail grid remains but most users interact with this picker. */
  onTogglePlatform: (platformId: string) => void;

  platformOverrides: Record<string, { text: string }>;
  onOverrideChange: (platform: string, text: string) => void;
  onOverridesReplace: (overrides: Record<string, { text: string }>) => void;
  onOverrideClear: (platform: string) => void;

  /** Active tab (controlled from parent so the parent can drive
   *  deep-links / studio-style "open tab X" from elsewhere). */
  activeTab: string | null;
  onActiveTabChange: (platform: string | null) => void;

  /** DOM id for the textarea — wired to the section's `<label>`. */
  textareaId: string;
}

const ComposerCaptionTabs: React.FC<Props> = ({
  caption,
  onCaptionChange,
  selectedPlatforms,
  onTogglePlatform,
  platformOverrides,
  onOverrideChange,
  onOverridesReplace,
  onOverrideClear,
  activeTab,
  onActiveTabChange,
  textareaId,
}) => {
  const { t } = useTranslation();
  const { theme } = useThemeStore();
  // Tone preference is local to the editor — we don't persist it on
  // the post model because it's a transient "how should AI rewrite"
  // hint, not a property of the content itself.
  const [tone, setTone] = useState<string>('');

  // Keep the active tab in sync with the selected-platform set.
  // Mirrors StudioPostCanvas's useEffect (auto-pick first when the
  // user toggles a platform on, or roll focus to the next one when
  // the current tab's platform is deselected).
  useEffect(() => {
    if (selectedPlatforms.length === 0) {
      if (activeTab !== null) {
        onActiveTabChange(null);
      }
      return;
    }
    if (!activeTab || !selectedPlatforms.includes(activeTab)) {
      onActiveTabChange(selectedPlatforms[0]);
    }
  }, [selectedPlatforms, activeTab, onActiveTabChange]);

  const platformStatus = useMemo(() => {
    return selectedPlatforms
      .map((pid) => {
        const plat = PLATFORMS.find((p) => p.id === pid);
        if (!plat) {
          return null;
        }
        const override = platformOverrides[pid];
        const hasOverride = !!override?.text;
        const effectiveText = hasOverride ? override.text : caption;
        const charCount = effectiveText.length;
        return {
          id: pid,
          name: plat.name,
          icon: plat.icon,
          maxChars: plat.maxChars,
          charCount,
          isOverLimit: charCount > plat.maxChars,
          hasOverride,
          effectiveText,
        };
      })
      .filter(Boolean) as Array<{
      id: string;
      name: string;
      icon: string;
      maxChars: number;
      charCount: number;
      isOverLimit: boolean;
      hasOverride: boolean;
      effectiveText: string;
    }>;
  }, [selectedPlatforms, platformOverrides, caption]);

  const active = platformStatus.find((p) => p.id === activeTab);
  const hasOverLimit = platformStatus.some((p) => p.isOverLimit);

  // Per-tab generation: rewrite the shared caption specifically for
  // the currently-active platform. Different from Auto-Adapt (which
  // fans out to every selected platform at once). Useful for "I want
  // to iterate on the Instagram version without touching Facebook."
  const generateForPlatformMutation = useMutation({
    mutationFn: (platformId: string) =>
      aiAPI.rewrite({
        original_text: caption,
        target_platform: platformId,
        ...(tone ? { tone } : {}),
      }),
    onSuccess: (data, platformId) => {
      const text = data.data?.rewritten || '';
      if (!text) {
        toast.error(t('comp_rewrite_empty', 'Model returned empty content'));
        return;
      }
      onOverrideChange(platformId, text);
      // Mirror to the shared caption too so downstream preflight /
      // first-comment char counts stay consistent with what the user
      // sees in the active tab.
      onCaptionChange(text);
      const platformName =
        PLATFORMS.find((p) => p.id === platformId)?.name || platformId;
      toast.success(
        t('comp_rewrite_for_platform_done', '{{name}} version generated', {
          name: platformName,
        })
      );
    },
    onError: (error: unknown) => {
      toast.error(
        getErrorMessage(error, t('comp_rewrite_failed', 'Failed to generate caption'))
      );
    },
  });

  const adaptMutation = useMutation({
    mutationFn: () =>
      aiAPI.adaptForPlatforms({
        original_text: caption,
        target_platforms: selectedPlatforms,
        // Empty string = let the backend pick; any other value forwards
        // the user's explicit tone preference.
        ...(tone ? { tone } : {}),
      }),
    onSuccess: (data) => {
      const overrides = data.data?.overrides || {};
      const next: Record<string, { text: string }> = {};
      for (const [platform, text] of Object.entries(overrides)) {
        if (typeof text === 'string' && text.trim()) {
          next[platform] = { text };
        }
      }
      onOverridesReplace(next);
      const count = Object.keys(next).length;
      if (count > 0) {
        toast.success(
          t('comp_adapted_platforms', 'Content adapted for {{count}} platforms', { count })
        );
      } else {
        toast.success(
          t('comp_no_adapt_needed', 'Content fits all platforms — no changes needed')
        );
      }
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, t('comp_adapt_failed', 'Failed to adapt content')));
    },
  });

  // Inline platform picker — rendered BOTH in the empty state (with
  // prominent styling so the user can't miss it) and above the tabs
  // so the user can add/remove platforms without hunting for the
  // right-rail grid. Mirrors the Studio pattern where platform pills
  // live right in the main canvas flow.
  const platformPicker = (
    <div
      role="group"
      aria-label={t(
        'comp_caption_platform_picker_label',
        'Select platforms to publish to'
      )}
      className="flex flex-wrap gap-2"
    >
      {PLATFORMS.map((p) => {
        const isSelected = selectedPlatforms.includes(p.id);
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => onTogglePlatform(p.id)}
            aria-pressed={isSelected}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
              isSelected
                ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400'
            }`}
          >
            <span className="text-sm leading-none">{p.icon}</span>
            <span>{p.name}</span>
          </button>
        );
      })}
    </div>
  );

  // Empty state — no platforms picked yet. Render the picker front-
  // and-centre (not hidden on the right rail) so the user's next
  // click is obvious.
  if (selectedPlatforms.length === 0) {
    return (
      <div className="w-full p-4 border border-dashed border-slate-300 rounded-lg bg-slate-50">
        <p className="text-sm font-semibold text-slate-800">
          {t(
            'comp_caption_no_platform_title',
            'Pick at least one platform to start writing'
          )}
        </p>
        <p className="mt-1 mb-4 text-xs text-slate-500">
          {t(
            'comp_caption_no_platform_hint_inline',
            'Each platform gets its own caption and preview. Click below to pick one or more.'
          )}
        </p>
        {platformPicker}
      </div>
    );
  }

  const handleActiveTextChange = (nextText: string) => {
    if (!active) {
      return;
    }
    onOverrideChange(active.id, nextText);
    // Mirror into the shared caption so downstream systems
    // (first-comment char check, single-platform preflight, legacy
    // publish fallback) see a sensible value. `platform_overrides`
    // remains the source of truth for rendering + publish.
    onCaptionChange(nextText);
  };

  return (
    <div className="space-y-2">
      {/* Inline platform picker — selected pills fill, unselected are
          outline. Clicking toggles selection, which auto-syncs the
          tab row below. Users no longer need to scroll to the right
          rail to add or remove a platform. */}
      <div className="px-1">
        {platformPicker}
      </div>

      <div className="rounded-lg border border-slate-200 overflow-hidden">
      {/* Tab row — one button per selected platform */}
      <div className="flex items-center justify-between gap-2 bg-slate-50 border-b border-slate-200 px-2 py-1.5">
        <div
          role="tablist"
          aria-label={t('comp_caption_tabs_label', 'Platform caption tabs')}
          className="flex items-center gap-1 overflow-x-auto"
        >
          {platformStatus.map((p) => {
            const isActive = activeTab === p.id;
            return (
              <button
                key={p.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls={`${textareaId}-panel`}
                onClick={() => onActiveTabChange(p.id)}
                className={`
                  flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium
                  transition-colors whitespace-nowrap
                  ${
                    isActive
                      ? 'bg-white shadow-sm border border-slate-200 text-slate-900'
                      : 'text-slate-600 hover:bg-white/60 border border-transparent'
                  }
                `}
              >
                <span className="text-sm">{p.icon}</span>
                <span>{p.name}</span>
                {p.hasOverride && (
                  <span
                    className="w-1.5 h-1.5 rounded-full bg-emerald-500"
                    title={t('comp_caption_customized', 'Customized')}
                    aria-hidden="true"
                  />
                )}
                {p.isOverLimit && (
                  <AlertTriangle
                    className="h-3 w-3 text-red-500"
                    aria-label={t('comp_caption_over_limit', 'Over character limit')}
                  />
                )}
                <span
                  className={`text-[10px] tabular-nums ${
                    p.isOverLimit ? 'text-red-500 font-semibold' : 'text-slate-400'
                  }`}
                >
                  {p.charCount}/{p.maxChars}
                </span>
              </button>
            );
          })}
        </div>

        {/* Tone + Auto-Adapt. Tone picker is always available so the
            user can pre-select their voice before clicking Adapt; the
            Adapt button itself surfaces only when at least one
            platform is over-limit so we don't nag when nothing's
            broken. */}
        <div className="flex items-center gap-1.5 shrink-0">
          <label
            htmlFor="composer-tone-picker"
            className="sr-only"
          >
            {t('comp_tone_label', 'Rewrite tone')}
          </label>
          <select
            id="composer-tone-picker"
            value={tone}
            onChange={(e) => setTone(e.target.value)}
            disabled={adaptMutation.isPending}
            className="text-xs border border-slate-300 rounded-md px-2 py-1 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            title={t('comp_tone_title', 'Tone for AI rewrite')}
          >
            {TONE_OPTIONS.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {t(opt.labelKey, opt.fallback)}
              </option>
            ))}
          </select>
          {hasOverLimit && !adaptMutation.isPending && (
            <button
              type="button"
              onClick={() => adaptMutation.mutate()}
              disabled={!caption.trim()}
              style={{ backgroundColor: theme.hex.primary }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold text-white transition-colors disabled:opacity-40"
            >
              <Sparkles className="h-3 w-3" aria-hidden="true" />
              {t('comp_adapt_all', 'Auto-Adapt')}
            </button>
          )}
        </div>
        {adaptMutation.isPending && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-500 shrink-0">
            <div className="animate-spin h-3 w-3 border-2 border-slate-300 border-t-slate-600 rounded-full" />
            {t('comp_adapting', 'Adapting...')}
          </div>
        )}
      </div>

      {/* Active-tab editor */}
      {active && (
        <div
          id={`${textareaId}-panel`}
          role="tabpanel"
          aria-labelledby={`${textareaId}-tab-${active.id}`}
          className="p-3 bg-white"
        >
          <div className="flex items-center justify-between mb-2 gap-2">
            <p className="text-xs font-medium text-slate-600 truncate">
              {active.hasOverride
                ? t('comp_platform_custom', '{{name}} (customized)', { name: active.name })
                : t('comp_platform_shared', '{{name}} — using shared caption', {
                    name: active.name,
                  })}
            </p>
            <div className="flex items-center gap-2 shrink-0">
              {/* Per-platform AI generation. Rewrites the shared
                  caption for the active platform only — users
                  asked for this because Auto-Adapt fans out to
                  everything at once and they want to iterate on
                  one tab without disturbing the others. */}
              <button
                type="button"
                onClick={() => generateForPlatformMutation.mutate(active.id)}
                disabled={
                  !caption.trim() || generateForPlatformMutation.isPending
                }
                aria-busy={generateForPlatformMutation.isPending}
                title={
                  !caption.trim()
                    ? t(
                        'comp_rewrite_needs_caption',
                        'Write a shared caption first'
                      )
                    : t(
                        'comp_rewrite_for_platform_tip',
                        'Generate a {{name}}-optimized caption',
                        { name: active.name }
                      )
                }
                className="flex items-center gap-1 text-xs font-medium text-emerald-600 hover:text-emerald-700 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors"
              >
                <Sparkles className="h-3 w-3" aria-hidden="true" />
                {generateForPlatformMutation.isPending &&
                generateForPlatformMutation.variables === active.id
                  ? t('comp_rewrite_generating', 'Generating…')
                  : t('comp_rewrite_for_platform', 'Generate for {{name}}', {
                      name: active.name,
                    })}
              </button>
              {active.hasOverride && (
                <button
                  type="button"
                  onClick={() => onOverrideClear(active.id)}
                  className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors"
                >
                  <RotateCcw className="h-3 w-3" aria-hidden="true" />
                  {t('comp_revert_shared', 'Revert to shared')}
                </button>
              )}
            </div>
          </div>
          <textarea
            id={textareaId}
            value={active.effectiveText}
            onChange={(e) => handleActiveTextChange(e.target.value)}
            placeholder={t(
              'comp_platform_placeholder',
              'Write {{name}}-specific content...',
              { name: active.name }
            )}
            className={`w-full h-40 p-3 border rounded-lg focus:ring-2 focus:ring-emerald-500/30 focus:border-transparent resize-y text-sm leading-relaxed transition-colors ${
              active.isOverLimit
                ? 'border-red-300 bg-red-50/50'
                : 'border-slate-200 bg-white'
            }`}
          />
          <div className="flex items-center justify-between mt-1.5 gap-3">
            {/* Studio-parity visual gauge: colour + icon make the
                over-limit state impossible to miss, where the old
                text-only count was easy to overlook. */}
            <div className="flex-1 min-w-0">
              <StudioCaptionCharCount
                text={active.effectiveText}
                platforms={selectedPlatforms}
                activeTab={active.id}
              />
            </div>
            {active.isOverLimit && (
              <button
                type="button"
                onClick={() => {
                  const truncated =
                    active.effectiveText.slice(0, active.maxChars - 3) + '...';
                  onOverrideChange(active.id, truncated);
                  onCaptionChange(truncated);
                }}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium shrink-0"
              >
                {t('comp_quick_shorten', 'Quick shorten')}
              </button>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default ComposerCaptionTabs;
